import {
  AlertCircle,
  Download,
  Eye,
  Heart,
  Mail,
  Modal,
  Phone,
  Plus, useDebounce, User,
  Users,
  formatPhoneNumberForDisplay
} from "@ews-admin/global-design-system";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { LicenceGate } from "../../components/atoms/LicenceGate";
import { LicenceModal } from "../../components/molecules/LicenceModal/LicenceModal";
import { PageHeader } from "../../components/molecules/PageHeader/PageHeader";
import { Pagination } from "../../components/molecules/Pagination/Pagination";

import { PatientFormSchema } from "@/schemas/patient.schema";
import { CivilityEnum } from "@/types/user.definition";
import { Button } from "@ews-admin/global-design-system";
import { AppointmentForm } from "../../components/molecules/AppointmentForm";
import { PatientForm } from "../../components/molecules/PatientForm/PatientForm";
import { PatientsFilter } from "../../components/molecules/PatientsFilter";
import { PatientAppointmentsModal } from "../../components/organims/PatientAppointmentsModal";
import useFacilityUser from "../../hooks/useFacilityUser";
import { useHasLicence } from "../../hooks/useHasLicence";
import { appointmentApi } from "../../services/appointment.api";
import patientApi from "../../services/patient.api";
import { fetchStatsRequest } from "../../store/reducers/dashboardSlice";
import {
  hideLoader,
  resetLoader,
  showLoader,
} from "../../store/reducers/loaderSlice";
import {
  addPatientToList,
  fetchPatientsRequest,
  setCurrentPage,
  setFilterAgeRange,
  setFilterGender,
  setIsCreatingAppointment,
  setIsCreatingPatient,
  setItemsPerPage,
  setNewPatientId,
  setSearchTerm,
  setSelectedPatient,
  setShowAppointmentForm,
  setShowAppointments,
  setShowFilters,
  setShowMedicalRecords,
  setShowPatientForm,
} from "../../store/reducers/patientSlice";
import { RootState } from "../../store/store";
import { DEFAULT_PATIENT_IMAGE_FEMALE, DEFAULT_PATIENT_IMAGE_MALE, MedicalRecord, Patient } from "../../types/patient.definition";
import { Order, Query } from "../../types/search.definition";
import { convertToDateISOString } from "../../utils/date.utils";

export const ManagePatients: React.FC = () => {
  const { t } = useTranslation();
  const facilityUser = useFacilityUser();
  const hasLicence = useHasLicence();
  const [showLicenceModal, setShowLicenceModal] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const facilityId = Number(facilityUser.healthcareFacility?.id);


  // Get state from Redux store
  const {
    patients,
    loading: isLoading,
    error,
    searchTerm,
    filterGender,
    filterAgeRange,
    currentPage,
    itemsPerPage,
    totalItems,
    showFilters,
    showPatientForm,
    showAppointmentForm,
    showMedicalRecords,
    showAppointments,
    selectedPatient,
    isCreatingPatient,
    isCreatingAppointment,
    newPatientId,
  } = useSelector((state: RootState) => state.patients);

  // Local state for UI messages
  const [successMessage] = useState<string | null>(null);
  const [showCreateAppointmentModal, setShowCreateAppointmentModal] =
    useState(false);

  // Debounce search term to improve performance
  const debouncedSearchTerm = useDebounce(searchTerm);

  // Use ref to track last request and prevent duplicate calls
  const lastRequestRef = useRef<string>("");

  // Reset loader on component mount to prevent stuck loader state
  useEffect(() => {
    // Reset global loader when component mounts to clear any stuck state
    // This ensures that if the loader was stuck from a previous session or error,
    // it gets cleared on page reload
    dispatch(resetLoader());

    // Cleanup: ensure loader is hidden when component unmounts
    return () => {
      dispatch(hideLoader());
    };
  }, [dispatch]);

  // Fetch patients when filters, pagination, or facility changes
  useEffect(() => {
    if (!facilityId) return;

    // Build query with filters
    const query: Query = {
      keyword: debouncedSearchTerm || "",
    };

    // Add gender filter if selected
    if (filterGender) {
      query.civility = filterGender;
    }

    // Add age range filter if selected
    if (filterAgeRange) {
      const [minAge, maxAge] = filterAgeRange.split("-").map(Number);

      if (filterAgeRange.includes("+")) {
        // Handle "65+" case
        query.minAge = 65;
      } else if (minAge || maxAge) {
        // Handle "0-18", "19-30", etc. cases
        query.minAge = minAge;
        query.maxAge = maxAge;
      }
    }

    const request = {
      sortBy: {
        property: "user.firstName",
        order: Order.ASC,
      },
      query,
      paginationSettings: {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
      },
    };

    // Create a unique key for this request to prevent duplicate calls
    const requestKey = JSON.stringify({
      facilityId,
      keyword: debouncedSearchTerm,
      filterGender,
      filterAgeRange,
      page: currentPage,
      limit: itemsPerPage,
    });

    // Only dispatch if this is a different request
    // Note: We don't check !isLoading here because lastRequestRef already prevents duplicates
    // and checking isLoading might block legitimate requests if the state is stuck
    if (lastRequestRef.current !== requestKey) {
      lastRequestRef.current = requestKey;
      dispatch(
        fetchPatientsRequest({
          facilityId,
          searchRequest: request,
        }),
      );
    }
  }, [
    debouncedSearchTerm,
    filterGender,
    filterAgeRange,
    currentPage,
    itemsPerPage,
    facilityId,
    dispatch,
    isLoading,
  ]);

  // Handle patientId query parameter for direct navigation
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const patientId = searchParams.get("patientId");

    if (patientId && patients.length > 0) {
      const patient = patients.find((p) => p.id === patientId);
      if (patient) {
        // Navigate to patient details page instead of opening modal
        navigate(`/patients/${patientId}`);
        // Clear the query parameter after navigation
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete("patientId");
        window.history.replaceState({}, "", newUrl.toString());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patients, location.search, navigate]);

  // Handle page change
  const handlePageChange = (page: number) => {
    dispatch(setCurrentPage(page));
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    dispatch(setItemsPerPage(newItemsPerPage));
  };

  let defaultImage = DEFAULT_PATIENT_IMAGE_MALE;

  // Handle patient selection
  const handlePatientSelect = (patient: Patient) => {
    if (patient.id) {
      // Navigate to patient details page instead of opening modal
      navigate(`/patients/${patient.id}`);
    }

    defaultImage =
      patient?.civility === CivilityEnum.MR
        ? DEFAULT_PATIENT_IMAGE_MALE
        : DEFAULT_PATIENT_IMAGE_FEMALE;
  };

  // Handle patient creation
  const handleCreatePatient = async (patientData: PatientFormSchema) => {
    dispatch(setIsCreatingPatient(true));

    try {
      const payload: Partial<Patient> = {
        ...patientData,
        dateOfBirth: convertToDateISOString(patientData.dateOfBirth as string),
      };
      const newPatient = await patientApi.createPatient(facilityId, payload);

      // Add the new patient to Redux store
      dispatch(addPatientToList(newPatient));

      // Set the new patient ID for appointment creation
      dispatch(setNewPatientId(newPatient.id));

      // Refresh facility statistics after patient creation
      if (facilityUser.healthcareFacility?.id) {
        dispatch(
          fetchStatsRequest({ facilityId: facilityUser.healthcareFacility.id }),
        );
      }

      // Refresh the patient list
      const query: Query = {
        keyword: debouncedSearchTerm || "",
      };
      if (filterGender) {
        query.civility = filterGender;
      }
      if (filterAgeRange) {
        const [minAge, maxAge] = filterAgeRange.split("-").map(Number);
        if (filterAgeRange.includes("+")) {
          query.minAge = 65;
        } else if (minAge || maxAge) {
          query.minAge = minAge;
          query.maxAge = maxAge;
        }
      }
      const request = {
        sortBy: {
          property: "user.firstName",
          order: Order.ASC,
        },
        query,
        paginationSettings: {
          limit: itemsPerPage,
          offset: (currentPage - 1) * itemsPerPage,
        },
      };
      // Reset the last request ref to allow refresh
      lastRequestRef.current = "";
      dispatch(
        fetchPatientsRequest({
          facilityId,
          searchRequest: request,
        }),
      );

      // Close patient form and show confirmation modal
      dispatch(setShowPatientForm(false));
      setShowCreateAppointmentModal(true);
    } catch (err) {
      console.error("Error creating patient:", err);
    } finally {
      dispatch(setIsCreatingPatient(false));
    }
  };

  // Handle appointment creation
  const handleCreateAppointment = async (
    appointmentData: Record<string, unknown>,
  ) => {
    dispatch(setIsCreatingAppointment(true));

    try {
      // Create the appointment using the API
      await appointmentApi.createAppointment(
        appointmentData as Parameters<
          typeof appointmentApi.createAppointment
        >[0],
      );

      // Close the appointment form
      dispatch(setShowAppointmentForm(false));
      dispatch(setNewPatientId(null));

      // Refresh facility statistics after appointment creation
      if (facilityUser.healthcareFacility?.id) {
        dispatch(
          fetchStatsRequest({ facilityId: facilityUser.healthcareFacility.id }),
        );
      }
    } catch (err) {
      console.error("Error creating appointment:", err);
      // You might want to show an error message to the user here
    } finally {
      dispatch(setIsCreatingAppointment(false));
    }
  };

  // Handle closing appointment form
  const handleCloseAppointmentForm = () => {
    dispatch(setShowAppointmentForm(false));
    dispatch(setNewPatientId(null));
    setShowCreateAppointmentModal(false);
  };

  // Handle confirmation modal - user wants to create appointment
  const handleConfirmCreateAppointment = () => {
    setShowCreateAppointmentModal(false);
    dispatch(setShowAppointmentForm(true));
  };

  // Handle confirmation modal - user skips creating appointment
  const handleSkipCreateAppointment = () => {
    setShowCreateAppointmentModal(false);
    dispatch(setNewPatientId(null));
  };

  // Handle opening patient form
  const handleOpenPatientForm = () => {
    dispatch(setShowPatientForm(true));
  };

  // Handle closing forms
  const handleCloseForms = () => {
    dispatch(setShowPatientForm(false));
    dispatch(setShowAppointmentForm(false));
    dispatch(setNewPatientId(null));
  };

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth: string) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filterGender, filterAgeRange]);

  // Export patients to CSV
  const handleExportPatients = () => {
    if (patients.length === 0) {
      return;
    }

    // Show loader while preparing export
    dispatch(showLoader("Preparing export..."));

    // Use setTimeout to allow the loader to render before blocking the UI
    setTimeout(() => {
      try {
        // Define CSV headers
        const headers = [
          "ID",
          "First Name",
          "Last Name",
          "Email",
          "Phone Number",
          "Date of Birth",
          "Age",
          "Gender",
          "Address",
          "City",
          "Postal Code",
          "Country",
          "Allergies",
          "Medications",
          "Blood Type",
          "Emergency Contact Name",
          "Emergency Contact Phone",
          "Last Visit",
          "Status",
        ];

        // Convert patients data to CSV rows
        const rows = patients.map((patient) => {
          const age = patient.dateOfBirth
            ? calculateAge(patient.dateOfBirth)
            : "N/A";
          const allergies = patient.allergies?.join("; ") || "None";
          const medications = patient.medications?.join("; ") || "None";
          const lastVisit = patient.lastVisit
            ? new Date(patient.lastVisit).toLocaleDateString()
            : "No visits";

          // Get primary emergency contact (priority 0 = highest priority)
          const primaryContact =
            patient.emergencyContacts?.find((contact) => contact.priority === 0) ||
            patient.emergencyContacts?.[0];

          // Determine status
          let status = "Inactive";
          if (patient.disabled) {
            status = "Inactive";
          } else if (
            patient.upcomingAppointments &&
            patient.upcomingAppointments.length > 0
          ) {
            status = "Has Appointments";
          } else if (
            patient.lastVisit &&
            new Date(patient.lastVisit) >
            new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
          ) {
            status = "Recent Visit";
          }
          const name = `${primaryContact?.firstName} ${primaryContact?.lastName}`;
          return [
            patient.id || "",
            patient.firstName || "",
            patient.lastName || "",
            patient.email || "",
            patient.phoneNumber || "",
            patient.dateOfBirth
              ? new Date(patient.dateOfBirth).toLocaleDateString()
              : "",
            age,
            patient.civility || "",
            patient.location?.address || "",
            patient.location?.city || "",
            patient.location?.zip || "",
            patient.location?.country || "",
            allergies,
            medications,
            patient.bloodType || "",
            name || "",
            primaryContact?.phoneNumber || "",
            lastVisit,
            status,
          ];
        });

        // Escape CSV values that contain commas, quotes, or newlines
        const escapeCSVValue = (value: string) => {
          if (
            value.includes(",") ||
            value.includes('"') ||
            value.includes("\n")
          ) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        };

        // Build CSV content
        const csvContent = [
          headers.map(escapeCSVValue).join(","),
          ...rows.map((row) =>
            row.map((value) => escapeCSVValue(String(value))).join(","),
          ),
        ].join("\n");

        // Create blob and download
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);

        // Generate filename with timestamp
        const timestamp = new Date().toISOString().split("T")[0];
        const filename = `patients_export_${timestamp}.csv`;

        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error exporting patients:", error);
      } finally {
        // Hide loader after export is complete
        dispatch(hideLoader());
      }
    }, 100);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <PageHeader
        title={t("patient.manage.title")}
        description={t("patient.manage.subtitle")}
        icon={Users}
        actions={
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExportPatients}
              disabled={patients.length === 0 || isLoading}
            >
              <Download className="mr-2 w-4 h-4" />
              {t("patient.manage.export")}
            </Button>
            <LicenceGate
              isLicenced={hasLicence}
              tooltipText={t("licence.requiredTooltip")}
              onRestrictedClick={() => setShowLicenceModal(true)}
            >
              <Button size="sm" onClick={handleOpenPatientForm}>
                <Plus className="mr-2 w-4 h-4" />
                {t("patient.manage.addPatient")}
              </Button>
            </LicenceGate>
          </div>
        }
      />

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="p-4 mb-4 text-sm text-green-800 bg-green-50 rounded-lg border border-green-200">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="p-4 mb-4 text-sm text-red-800 bg-red-50 rounded-lg border border-red-200">
          {error}
        </div>
      )}

      {/* Search and Filters */}
      <PatientsFilter
        searchTerm={searchTerm}
        setSearchTerm={(term) => dispatch(setSearchTerm(term))}
        showFilters={showFilters}
        setShowFilters={(show) => dispatch(setShowFilters(show))}
        filterGender={filterGender}
        setFilterGender={(gender) => dispatch(setFilterGender(gender))}
        filterAgeRange={filterAgeRange}
        setFilterAgeRange={(range) => dispatch(setFilterAgeRange(range))}
        debouncedSearchTerm={debouncedSearchTerm}
      />

      {/* Patients List */}
      <div className="space-y-4">
        {patients.map((patient) => (
          <div key={patient.id} className="p-6 card-base">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <img
                      src={
                        patient.photoUrl || defaultImage
                      }
                      alt={`${patient.firstName} ${patient.lastName}`}
                      className="object-cover w-12 h-12 rounded-full border-2 border-gray-200"
                    />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {patient.firstName} {patient.lastName}
                    </h3>
                    <div className="flex items-center mt-1 space-x-2">
                      <span className="px-2 py-1 text-xs font-medium text-gray-800 bg-gray-100 rounded-full border border-gray-200">
                        {t("patient.manage.card.id")} {patient.id}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4 lg:grid-cols-3">
                  <div className="flex items-center text-sm text-gray-600">
                    <User className="mr-2 w-4 h-4 text-gray-400" />
                    {patient.age != null
                      ? `${patient.age} ${t("patient.manage.card.years")}`
                      : patient.dateOfBirth
                        ? `${calculateAge(patient.dateOfBirth)} ${t("patient.manage.card.years")}`
                        : t("patient.manage.card.ageNotSpecified")}
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Phone className="mr-2 w-4 h-4 text-gray-400" />
                    {formatPhoneNumberForDisplay(patient.phoneNumber)}
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Mail className="mr-2 w-4 h-4 text-gray-400" />
                    {patient.email}
                  </div>
                </div>

                {patient.allergies && patient.allergies.length > 0 && (
                  <div className="flex items-center mt-3 text-sm text-orange-600">
                    <AlertCircle className="mr-2 w-4 h-4" />
                    {t("patient.manage.card.allergies")}{" "}
                    {patient.allergies.join(", ")}
                  </div>
                )}
              </div>

              <div className="flex mt-4 space-x-2 md:mt-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePatientSelect(patient)}
                >
                  <Eye className="mr-2 w-4 h-4" />
                  {t("patient.manage.viewDetails")}
                </Button>
              </div>
            </div>
          </div>
        ))}

        {patients.length === 0 && !isLoading && (
          <div className="py-12 text-center card-base">
            <Users className="mx-auto w-12 h-12 text-neutral-400" />
            <h3 className="mt-2 text-sm font-medium text-neutral-900">
              {t("patient.manage.noPatients")}
            </h3>
            <p className="mt-1 text-sm text-neutral-500">
              {t("patient.manage.adjustFilters")}
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalItems > 0 && (
        <Pagination
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          isLoading={isLoading}
        />
      )}

      {/* Medical Records Modal */}
      {showMedicalRecords && selectedPatient && (
        <MedicalRecordsModal
          patient={selectedPatient}
          onClose={() => {
            dispatch(setShowMedicalRecords(false));
            dispatch(setSelectedPatient(null));
          }}
        />
      )}

      {/* Appointments Modal */}
      {showAppointments && selectedPatient && (
        <PatientAppointmentsModal
          patient={selectedPatient}
          onClose={() => {
            dispatch(setShowAppointments(false));
            dispatch(setSelectedPatient(null));
          }}
        />
      )}

      {/* Patient Form Modal */}
      {showPatientForm && (
        <PatientForm
          onSubmit={handleCreatePatient}
          onCancel={handleCloseForms}
          isSubmitting={isCreatingPatient}
        />
      )}

      {/* Confirmation Modal - Create Appointment */}
      {showCreateAppointmentModal && newPatientId && (
        <Modal
          isOpen
          onClose={handleSkipCreateAppointment}
          title={t("patient.manage.createAppointment.title")}
          variant="info"
          size="md"
          primaryAction={t("patient.manage.createAppointment.create")}
          secondaryAction={t("patient.manage.createAppointment.skip")}
          onPrimaryAction={handleConfirmCreateAppointment}
          onSecondaryAction={handleSkipCreateAppointment}
          isLoading={isCreatingAppointment}
          closeOnOverlayClick={!isCreatingAppointment}
        >
          <p className="text-sm text-neutral-600">
            {t("patient.manage.createAppointment.message")}
          </p>
        </Modal>
      )}

      {/* Appointment Form Modal */}
      {showAppointmentForm && newPatientId && facilityId && (
        <AppointmentForm
          onSubmit={handleCreateAppointment}
          onCancel={handleCloseAppointmentForm}
          isSubmitting={isCreatingAppointment}
          facilityId={facilityId}
          initialPatientId={newPatientId}
        />
      )}

      <LicenceModal
        isOpen={showLicenceModal}
        onClose={() => setShowLicenceModal(false)}
      />
    </div>
  );
};

// Medical Records Modal Component
interface MedicalRecordsModalProps {
  patient: Patient;
  onClose: () => void;
}

const MedicalRecordsModal: React.FC<MedicalRecordsModalProps> = ({
  patient,
  onClose,
}) => {
  const { t } = useTranslation();
  const [records] = useState<MedicalRecord[]>(patient.medicalHistory || []);

  const getRecordTypeColor = (type: string) => {
    switch (type) {
      case "CONSULTATION":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "LAB_TEST":
        return "bg-green-100 text-green-800 border-green-200";
      case "PRESCRIPTION":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "SURGERY":
        return "bg-red-100 text-red-800 border-red-200";
      case "VACCINATION":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t("patient.manage.medicalRecords.title")}
      variant="info"
      size="4xl"
      contentClassName="max-h-[75vh] overflow-y-auto"
      primaryAction={t("patient.manage.medicalRecords.close")}
      onPrimaryAction={onClose}
    >
      <p className="mb-6 -mt-2 text-sm text-neutral-600">
        {patient.firstName} {patient.lastName}
      </p>

      <div className="space-y-4">
            {records.length > 0 ? (
              records.map((record) => (
                <div
                  key={record.id}
                  className="p-4 rounded-lg border border-neutral-200"
                >
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center space-x-3">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full border ${getRecordTypeColor(
                          record.type,
                        )}`}
                      >
                        {record.type.replace("_", " ")}
                      </span>
                      <span className="text-sm text-neutral-600">
                        {new Date(record.date).toLocaleDateString()}
                      </span>
                    </div>
                    <span className="text-sm text-neutral-600">
                      {t("patient.manage.medicalRecords.doctor")}{" "}
                      {record.doctor}
                    </span>
                  </div>

                  {record.diagnosis && (
                    <div className="mb-2">
                      <span className="text-sm font-medium text-neutral-700">
                        {t("patient.manage.medicalRecords.diagnosis")}
                      </span>
                      <span className="ml-2 text-sm text-neutral-600">
                        {record.diagnosis}
                      </span>
                    </div>
                  )}

                  {record.treatment && (
                    <div className="mb-2">
                      <span className="text-sm font-medium text-neutral-700">
                        {t("patient.manage.medicalRecords.treatment")}
                      </span>
                      <span className="ml-2 text-sm text-neutral-600">
                        {record.treatment}
                      </span>
                    </div>
                  )}

                  {record.notes && (
                    <div>
                      <span className="text-sm font-medium text-neutral-700">
                        {t("patient.manage.medicalRecords.notes")}
                      </span>
                      <span className="ml-2 text-sm text-neutral-600">
                        {record.notes}
                      </span>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="py-8 text-center text-neutral-500">
                <Heart className="mx-auto mb-4 w-12 h-12 text-neutral-300" />
                <p>{t("patient.manage.medicalRecords.noRecords")}</p>
              </div>
            )}
      </div>
    </Modal>
  );
};
